/**
 * Sections Tab Component
 * Manages sections listing, search, create, and edit
 */

import emptyBoxAnimation from '@assets/lottie/empty-box.json';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import { selectEffectiveOutletId } from '@store/outlets';
import { Plus, Search } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import {
  selectAllSections,
  selectSectionsLoading,
} from '../../../store/sections/sectionSelectors';
import {
  createSectionThunk,
  updateSectionThunk,
} from '../../../store/sections/sectionThunks';
import { OutletSection } from '../../../types/section';
import { ONBOARDING_TOUR_TARGETS } from '../../../services/onboardingTourService';
import { EmptyState, LoadingState } from '../../common';
import { SectionFormModal } from './SectionFormModal';
import { SectionRow } from './SectionRow';

interface SectionsTabProps {
  canManage?: boolean;
  hasPrinterConfigForSection: (sectionId: number) => boolean;
  onConfigurePrinters: (section: OutletSection) => void;
  onViewTables: (section: OutletSection) => void;
}

export const SectionsTab: React.FC<SectionsTabProps> = ({
  canManage = true,
  hasPrinterConfigForSection,
  onConfigurePrinters,
  onViewTables,
}) => {
  const dispatch = useAppDispatch();
  const sections = useAppSelector(selectAllSections);
  const isLoading = useAppSelector(selectSectionsLoading);
  const outletId = useAppSelector(selectEffectiveOutletId);

  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<OutletSection | null>(
    null,
  );

  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return sections;
    const query = searchQuery.toLowerCase();
    return sections.filter((s) => s.name.toLowerCase().includes(query));
  }, [sections, searchQuery]);

  const handleCreate = useCallback(
    async (name: string, isHidden: boolean) => {
      if (!outletId || !canManage) return;
      await dispatch(
        createSectionThunk({ name, outlet_id: outletId, is_hidden: isHidden }),
      );
      setIsModalOpen(false);
    },
    [canManage, dispatch, outletId],
  );

  const handleUpdate = useCallback(
    async (name: string, isHidden: boolean) => {
      if (!editingSection || !canManage) return;
      await dispatch(
        updateSectionThunk({
          id: editingSection.id,
          name,
          is_hidden: isHidden,
        }),
      );
      setEditingSection(null);
    },
    [canManage, dispatch, editingSection],
  );

  const handleEdit = useCallback(
    (section: OutletSection) => {
      if (!canManage) return;
      setEditingSection(section);
    },
    [canManage],
  );

  if (isLoading && sections.length === 0) {
    return <LoadingState message="Loading sections..." />;
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            disabled={!outletId || !canManage}
            data-onboarding-tour={ONBOARDING_TOUR_TARGETS.sectionsAddButton}
            className="p-2 text-pos-primary hover:bg-pos-light/30 rounded-lg transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            title="Add Section"
          >
            <Plus size={20} />
          </button>
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search"
              aria-label="Search sections"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-pos-primary focus:border-pos-primary transition-colors w-40"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      {filteredSections.length === 0 ? (
        <EmptyState
          title="No sections found"
          icon="lottie"
          lottieData={emptyBoxAnimation}
          description={
            searchQuery
              ? 'Try adjusting your search query.'
              : 'Create your first section to organize your tables.'
          }
          primaryAction={
            !searchQuery && Boolean(outletId) && canManage
              ? {
                  dataTourId: ONBOARDING_TOUR_TARGETS.sectionsAddButton,
                  label: 'Add Section',
                  onClick: () => setIsModalOpen(true),
                  icon: <Plus size={18} />,
                }
              : undefined
          }
        />
      ) : (
        <div
          className="bg-white rounded-xl border border-gray-100 overflow-hidden"
          data-onboarding-tour={ONBOARDING_TOUR_TARGETS.sectionsTable}
        >
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Outlet
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Tables
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Printers
                </th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Active
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredSections.map((section, index) => (
                <SectionRow
                  key={section.id}
                  canManage={canManage}
                  hasPrinterConfig={hasPrinterConfigForSection(section.id)}
                  isTourPrimary={index === 0}
                  section={section}
                  showOutletColumn
                  onEdit={handleEdit}
                  onConfigurePrinters={onConfigurePrinters}
                  onViewTables={onViewTables}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      <SectionFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreate}
        isLoading={isLoading}
      />

      {/* Edit Modal */}
      <SectionFormModal
        isOpen={!!editingSection}
        onClose={() => setEditingSection(null)}
        onSubmit={handleUpdate}
        section={editingSection}
        isLoading={isLoading}
      />
    </div>
  );
};
